import React, { useEffect, useState, useMemo } from "react";
import TableSkeleton from "../../../../../../../../component/TableSkeleton";
import { Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import { useOutletContext, useNavigate } from "react-router";
import EmptyState from "../../../../../../../../component/EmptyState";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../../../component/listing";
import { getAllExpense } from "../../../../../../../../services/expense";
import { tokens } from "../../../../../../../../theme";
import RecordStat, { RecordStatRow } from "../RecordStat";

const TONE = { pending: "warning", approved: "success", rejected: "danger" };
const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const ProfileExpenses = () => {
  const [, employeeId] = useOutletContext();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getAllExpense();
        const list = (Array.isArray(res?.data) ? res.data : []).filter((e) => e.submitterId === employeeId && e.type === "ANY");
        if (active) setRows(list);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]);

  /**
   * The book opens this panel with four counters. Without them the tab drops
   * the reader into a list of claims with nothing saying what the person is
   * owed or how long it usually takes.
   */
  const stats = useMemo(() => {
    const sum = (list) => list.reduce((t, r) => t + (Number(r.amount) || 0), 0);
    const pending = rows.filter((r) => r.status === "pending");
    const reimbursed = rows.filter((r) => r.status === "approved" && (r.reimbursedAt || r.reimbursedInPayroll));
    const approvedUnpaid = rows.filter((r) => r.status === "approved" && !(r.reimbursedAt || r.reimbursedInPayroll));
    const rejected = rows.filter((r) => r.status === "rejected");
    return { pending, reimbursed, approvedUnpaid, rejected, sum };
  }, [rows]);

  if (loading) return (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
        <TableRow>
          <TableCell sx={headCellSx}>Category</TableCell>
          <TableCell sx={headCellSx}>Date</TableCell>
          <TableCell align="right" sx={headCellSx}>Amount</TableCell>
          <TableCell sx={headCellSx}>Status</TableCell>
        </TableRow>
      </TableHead>
            <TableBody><TableSkeleton columns={4} /></TableBody>
          </Table>
        );
  if (rows.length === 0) return <EmptyState title="No expense claims" description="This employee hasn't submitted any expenses." />;

  return (
    <>
      <RecordStatRow>
        <RecordStat
          label="Pending"
          value={money(stats.sum(stats.pending))}
          sub={`${stats.pending.length} claim${stats.pending.length === 1 ? "" : "s"} awaiting a decision`}
          tone={stats.pending.length ? tokens.warningText : undefined}
        />
        <RecordStat
          label="Approved, not paid"
          value={money(stats.sum(stats.approvedUnpaid))}
          sub={`${stats.approvedUnpaid.length} waiting on a payroll run`}
        />
        <RecordStat
          label="Reimbursed"
          value={money(stats.sum(stats.reimbursed))}
          sub={`${stats.reimbursed.length} claim${stats.reimbursed.length === 1 ? "" : "s"} paid out`}
        />
        <RecordStat
          label="Rejected"
          value={money(stats.sum(stats.rejected))}
          sub={stats.rejected.length ? `${stats.rejected.length} refused` : "none refused"}
        />
      </RecordStatRow>
    <Table sx={{ width: "100%", tableLayout: "fixed" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={headCellSx}>Category</TableCell>
          <TableCell sx={headCellSx}>Date</TableCell>
          <TableCell align="right" sx={headCellSx}>Amount</TableCell>
          <TableCell sx={headCellSx}>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r._id} hover onClick={() => navigate(`/app/expense-requests-details/${r._id}`)}
            sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r?.policyCateogary?.name || "—"}</TableCell>
            <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{fmt(r.createdDate || r.createdAt)}</TableCell>
            <TableCell align="right" sx={bodyCellSx}>{money(r.amount)}</TableCell>
            <TableCell sx={bodyCellSx}><StatusChip label={r.status} tone={TONE[r.status]} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </>
  );
};

export default ProfileExpenses;
